import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, NOMBRES, seededRandom } from "../spec";

const SHEET = "LG Desempeño";
const TABLE = "Evaluaciones";
const DEPTOS = ["Ventas", "Operaciones", "Finanzas", "Atención a clientes", "Logística"] as const;

const rnd = seededRandom(5120);
// Empleado, Departamento, Salario, Cumplimiento, Calificación, Faltas
const ROWS = NOMBRES.slice(0, 30).map((nombre) => [
  nombre,
  rnd.pick(DEPTOS),
  rnd.round(12000, 45000, 500),
  rnd.int(70, 135) / 100,
  rnd.int(45, 100),
  rnd.next() < 0.5 ? 0 : rnd.int(1, 6),
] as [string, string, number, number, number, number]);

type Row = (typeof ROWS)[number];
const nivel = (r: Row) =>
  r[4] >= 90 ? "Excelente" : r[4] >= 75 ? "Bueno" : r[4] >= 60 ? "Regular" : "Bajo";

export const spec: FuncionProjectSpec = {
  projectId: "funciones-si-conjunto",
  sheets: [
    {
      name: SHEET,
      title: "Evaluación de desempeño",
      subtitle: "Cierre semestral de Recursos Humanos — escribe tus fórmulas en las columnas amarillas",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Empleado", "Departamento", "Salario", "Cumplimiento", "Calificación", "Faltas"],
          rows: ROWS,
          answerColumns: ["Nivel", "Puntualidad", "Bono %", "Nivel con SI", "Plan de acción"],
          formats: { Salario: "$#,##0", Cumplimiento: "0%", "Bono %": "0%" },
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Nivel", fns: ["IFS"],
      expected: ROWS.map(nivel),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Puntualidad", fns: ["IFS"],
      expected: ROWS.map((r) => (r[5] === 0 ? "Perfecta" : r[5] <= 2 ? "Aceptable" : "Con faltas")),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Bono %", fns: ["IFS"],
      expected: ROWS.map((r) => (r[3] >= 1.2 ? 0.15 : r[3] >= 1 ? 0.1 : r[3] >= 0.9 ? 0.05 : 0)),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Nivel con SI", fns: ["IF"],
      expected: ROWS.map(nivel),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Plan de acción", fns: ["IFS", "AND"],
      expected: ROWS.map((r) =>
        r[4] < 60 && r[5] > 2
          ? "Baja"
          : r[4] < 60
          ? "Capacitación"
          : r[5] > 2
          ? "Llamada de atención"
          : "Ninguno"
      ),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-si-conjunto",
  title: "SI.CONJUNTO: Evaluación de desempeño",
  subtitle: "Muchas condiciones sin anidar",
  description:
    "Recursos Humanos cerró el semestre y necesita clasificar a 30 empleados: nivel de desempeño, puntualidad, bono y plan de acción.\n\nSI.CONJUNTO evalúa pares condición → resultado en orden y devuelve el primero que se cumpla. Es la forma moderna (y más legible) de escribir SI anidados.",
  topics: [
    "Pares condición → resultado",
    "VERDADERO como \"en cualquier otro caso\"",
    "SI.CONJUNTO frente a SI anidados",
    "Condiciones combinadas con Y",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Nivel de desempeño",
      description:
        conditions(
        "En la hoja <strong>LG Desempeño</strong>, columna <strong>Nivel</strong>, clasifica según la <strong>Calificación</strong>:",
        [
          "<strong>90 o más</strong> → <strong>Excelente</strong>",
          "<strong>75 o más</strong> → <strong>Bueno</strong>",
          "<strong>60 o más</strong> → <strong>Regular</strong>",
          "<strong>Menos de 60</strong> → <strong>Bajo</strong>",
        ],
        "Usa <strong>SI.CONJUNTO</strong>."
      ),
      solution: [
        `Selecciona ${F("G5")}.`,
        "SI.CONJUNTO recibe pares: condición1, resultado1, condición2, resultado2… Revisa de arriba hacia abajo y se detiene en la primera condición verdadera.",
        `Escribe ${F('=SI.CONJUNTO([@Calificación]>=90,"Excelente",[@Calificación]>=75,"Bueno",[@Calificación]>=60,"Regular",[@Calificación]<60,"Bajo")')}.`,
      ],
      expectedResult: "Excelente / Bueno / Regular / Bajo",
      finalFormula:
        '=SI.CONJUNTO([@Calificación]>=90,"Excelente",[@Calificación]>=75,"Bueno",[@Calificación]>=60,"Regular",[@Calificación]<60,"Bajo")',
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Puntualidad",
      description:
        conditions(
        "En la columna <strong>Puntualidad</strong>, según las <strong>Faltas</strong>:",
        [
          "<strong>0</strong> faltas → <strong>Perfecta</strong>",
          "<strong>Hasta 2</strong> → <strong>Aceptable</strong>",
          "<strong>Más de 2</strong> → <strong>Con faltas</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("H5")}.`,
        `Escribe ${F('=SI.CONJUNTO([@Faltas]=0,"Perfecta",[@Faltas]<=2,"Aceptable",[@Faltas]>2,"Con faltas")')}.`,
      ],
      expectedResult: "Perfecta / Aceptable / Con faltas",
      finalFormula: '=SI.CONJUNTO([@Faltas]=0,"Perfecta",[@Faltas]<=2,"Aceptable",[@Faltas]>2,"Con faltas")',
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Porcentaje de bono",
      description:
        conditions(
        "En la columna <strong>Bono %</strong>, según el <strong>Cumplimiento</strong>:",
        [
          "<strong>120% o más</strong> → <strong>15%</strong>",
          "<strong>100% o más</strong> → <strong>10%</strong>",
          "<strong>90% o más</strong> → <strong>5%</strong>",
          "<strong>En cualquier otro caso</strong> → <strong>0</strong>",
        ],
        "Para el último caso usa <strong>VERDADERO</strong> como condición."
      ),
      solution: [
        `Selecciona ${F("I5")}.`,
        "Si ninguna condición se cumple, SI.CONJUNTO devuelve #N/D. Para evitarlo, el último par usa VERDADERO, que siempre se cumple.",
        `Escribe ${F("=SI.CONJUNTO([@Cumplimiento]>=120%,15%,[@Cumplimiento]>=100%,10%,[@Cumplimiento]>=90%,5%,VERDADERO,0)")}.`,
      ],
      expectedResult: "15% / 10% / 5% / 0%",
      finalFormula:
        "=SI.CONJUNTO([@Cumplimiento]>=120%,15%,[@Cumplimiento]>=100%,10%,[@Cumplimiento]>=90%,5%,VERDADERO,0)",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: El mismo nivel, con SI anidados",
      description:
        "En la columna <strong>Nivel con SI</strong>, obtén el mismo resultado de la Tarea 1 pero usando <strong>SI anidados</strong> en lugar de SI.CONJUNTO. Compara cuál fórmula se lee mejor.",
      solution: [
        `Selecciona ${F("J5")}.`,
        "Cada SI nuevo va en el \"valor si falso\" del anterior; al final hay que cerrar tres paréntesis.",
        `Escribe ${F('=SI([@Calificación]>=90,"Excelente",SI([@Calificación]>=75,"Bueno",SI([@Calificación]>=60,"Regular","Bajo")))')}.`,
      ],
      expectedResult: "Igual a la columna Nivel",
      finalFormula:
        '=SI([@Calificación]>=90,"Excelente",SI([@Calificación]>=75,"Bueno",SI([@Calificación]>=60,"Regular","Bajo")))',
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Plan de acción",
      description:
        conditions(
        "En la columna <strong>Plan de acción</strong>, revisa en este orden:",
        [
          "Calificación <strong>menor a 60</strong> <em>y</em> <strong>más de 2</strong> faltas → <strong>Baja</strong>",
          "Solo calificación <strong>menor a 60</strong> → <strong>Capacitación</strong>",
          "Solo <strong>más de 2</strong> faltas → <strong>Llamada de atención</strong>",
          "En cualquier otro caso → <strong>Ninguno</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        "El caso más estricto (las dos condiciones) va primero; si lo pones al final nunca se alcanzaría.",
        `Escribe ${F('=SI.CONJUNTO(Y([@Calificación]<60,[@Faltas]>2),"Baja",[@Calificación]<60,"Capacitación",[@Faltas]>2,"Llamada de atención",VERDADERO,"Ninguno")')}.`,
      ],
      expectedResult: "Baja / Capacitación / Llamada de atención / Ninguno",
      finalFormula:
        '=SI.CONJUNTO(Y([@Calificación]<60,[@Faltas]>2),"Baja",[@Calificación]<60,"Capacitación",[@Faltas]>2,"Llamada de atención",VERDADERO,"Ninguno")',
    },
  ],
};
